import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  ToggleOn,
  ToggleOff
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AppLayout from '../../components/layout/AppLayout';
import DataTable from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import EmptyState from '../../components/common/EmptyState';
import emailTemplatesService from '../../services/emailTemplatesService';
import { TemplateType, TemplateTypeLabels } from '../../types/emailTemplate.types';
import type { EmailTemplate } from '../../types/emailTemplate.types';
import type { TableColumn } from '../../types/common.types';
import { getErrorMessage } from '../../utils/api';

const EmailTemplatesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string | null }>({
    open: false,
    id: null
  });
  const [toggleDialog, setToggleDialog] = useState<{
    open: boolean;
    template: EmailTemplate | null;
  }>({
    open: false,
    template: null
  });

  const [filters, setFilters] = useState<{ search: string; tipo: string }>({
    search: '',
    tipo: ''
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const { data: templates, isLoading } = useQuery({
    queryKey: ['email-templates', debouncedSearch, filters.tipo],
    queryFn: () => emailTemplatesService.getCurrent()
  });

  const filteredTemplates = (templates || []).filter((t) => {
    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      if (!t.nome.toLowerCase().includes(search) && !t.oggetto.toLowerCase().includes(search)) {
        return false;
      }
    }
    if (filters.tipo && t.tipo !== filters.tipo) return false;
    return true;
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailTemplatesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      setDeleteDialog({ open: false, id: null });
      enqueueSnackbar('Template eliminato con successo', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante l\'eliminazione'), { variant: 'error' });
    }
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, abilitato }: { id: string; abilitato: boolean }) =>
      emailTemplatesService.update(id, { abilitato }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
      const tmpl = toggleDialog.template;
      setToggleDialog({ open: false, template: null });
      enqueueSnackbar(`Template ${tmpl?.abilitato ? 'disabilitato' : 'abilitato'} con successo`, { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(getErrorMessage(error, 'Errore durante la modifica'), { variant: 'error' });
    }
  });

  const columns: TableColumn<EmailTemplate>[] = [
    {
      id: 'nome',
      label: 'Nome',
      minWidth: 180
    },
    {
      id: 'tipo',
      label: 'Tipo',
      minWidth: 160,
      format: (value) => {
        const tipo = value as string;
        return (
          <Chip
            label={TemplateTypeLabels[tipo as TemplateType] || tipo}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      }
    },
    {
      id: 'oggetto',
      label: 'Oggetto Email',
      minWidth: 250,
      format: (value) => {
        const oggetto = value as string;
        return oggetto.length > 50 ? `${oggetto.substring(0, 50)}...` : oggetto;
      }
    },
    {
      id: 'versione',
      label: 'Versione',
      minWidth: 80,
      align: 'center',
      format: (value) => (
        <Chip label={`v${value}`} size="small" variant="outlined" />
      )
    },
    {
      id: 'abilitato',
      label: 'Stato',
      minWidth: 100,
      format: (_, row) => (
        <Chip
          label={row.abilitato ? 'Attivo' : 'Disabilitato'}
          color={row.abilitato ? 'success' : 'default'}
          size="small"
        />
      )
    }
  ];

  const renderActions = (row: EmailTemplate) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Tooltip title="Visualizza/Modifica">
        <IconButton size="small" onClick={() => navigate(`/email-templates/${row.id}/edit`)}>
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={row.abilitato ? 'Disabilita' : 'Abilita'}>
        <IconButton
          size="small"
          color={row.abilitato ? 'warning' : 'success'}
          onClick={() => setToggleDialog({ open: true, template: row })}
          disabled={toggleMutation.isPending}
        >
          {row.abilitato ? <ToggleOff fontSize="small" /> : <ToggleOn fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Elimina">
        <IconButton
          size="small"
          color="error"
          onClick={() => setDeleteDialog({ open: true, id: row.id })}
          disabled={deleteMutation.isPending}
        >
          <Delete fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (isLoading) {
    return (
      <AppLayout>
        <LoadingSpinner message="Caricamento template email..." />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box>
        <Box sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Gestione Template Email
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/email-templates/new')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Nuovo Template
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Filtri</Typography>
          <form onSubmit={(e) => e.preventDefault()}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: { xs: 2, md: 3 }
            }}>
              <TextField
                label="Cerca"
                placeholder="Nome, oggetto..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                size="small"
                fullWidth
                helperText={filters.search !== debouncedSearch ? "Ricerca in corso..." : undefined}
              />
              <TextField
                select
                label="Tipo"
                value={filters.tipo}
                onChange={(e) => setFilters(prev => ({ ...prev, tipo: e.target.value }))}
                size="small"
                fullWidth
              >
                <MenuItem value="">Tutti</MenuItem>
                {Object.entries(TemplateTypeLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </TextField>
            </Box>
          </form>
        </Paper>

        <DataTable
          columns={columns}
          data={filteredTemplates}
          total={filteredTemplates.length}
          page={0}
          rowsPerPage={filteredTemplates.length}
          onPageChange={() => {}}
          onRowsPerPageChange={() => {}}
          actions={renderActions}
          loading={isLoading}
          emptyStateComponent={
            <EmptyState
              title="Nessun template trovato"
              message={
                debouncedSearch || filters.tipo
                  ? "Non ci sono template che corrispondono ai filtri selezionati."
                  : "Non hai ancora creato nessun template email. I template vengono usati per le notifiche automatiche."
              }
              actionLabel={!debouncedSearch && !filters.tipo ? "Crea Primo Template" : undefined}
              onAction={!debouncedSearch && !filters.tipo ? () => navigate('/email-templates/new') : undefined}
            />
          }
        />

        <ConfirmDialog
          open={deleteDialog.open}
          title="Conferma Eliminazione"
          message="Sei sicuro di voler eliminare questo template? Questa azione non puo essere annullata."
          confirmText="Elimina"
          confirmColor="error"
          onConfirm={() => deleteDialog.id && deleteMutation.mutate(deleteDialog.id)}
          onCancel={() => setDeleteDialog({ open: false, id: null })}
          loading={deleteMutation.isPending}
        />

        <ConfirmDialog
          open={toggleDialog.open}
          title={`Conferma ${toggleDialog.template?.abilitato ? 'Disabilitazione' : 'Abilitazione'}`}
          message={`Sei sicuro di voler ${toggleDialog.template?.abilitato ? 'disabilitare' : 'abilitare'} il template "${toggleDialog.template?.nome}"?`}
          confirmText={toggleDialog.template?.abilitato ? 'Disabilita' : 'Abilita'}
          confirmColor={toggleDialog.template?.abilitato ? 'warning' : 'success'}
          onConfirm={() => toggleDialog.template && toggleMutation.mutate({
            id: toggleDialog.template.id,
            abilitato: !toggleDialog.template.abilitato
          })}
          onCancel={() => setToggleDialog({ open: false, template: null })}
          loading={toggleMutation.isPending}
        />
      </Box>
    </AppLayout>
  );
};

export default EmailTemplatesListPage;
